import {
  HoverFeature,
  getHoverCellProps,
} from "@/components/DataTable/Features/hoverFeature";
import { DataTable, useTableDisplayState } from "@/components/DataTable/Table";
import { EmptyStateFeedBack } from "@/features/operation-package/-components/EmptyStateFeedBack";
import { cn } from "@/lib/cn";
import {
  type CellContext,
  type ColumnDef,
  type TableOptions,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { Row as TableRow } from "@tanstack/table-core";

type Props<T> = {
  /**
   * テーブルに表示するデータです。子要素を必ず保持してください。
   */
  data: T[];
  /**
   * ローディング中かどうかを表します。
   */
  isPending: boolean;
  /**
   * テーブルに表示するカラム定義です。
   */
  columns: ColumnDef<T>[];
  /**
   * テーブルのスタイルを指定します。
   */
  className?: string;
  /**
   * 行がクリックされた時のコールバック関数です。
   * @param row クリックされた行のデータ
   */
  onRowClick: (row: T) => void;
  emptyState: {
    title: string;
    description: string;
  };
  options?: Pick<
    TableOptions<T>,
    | "getSubRows"
    | "getExpandedRowModel"
    | "enableExpanding"
    | "state"
    | "initialState"
  >;
};

/**
 * Rowが最後の子要素かどうかを判定します。
 * 最後の子要素の場合のみborderを濃くするために使用します。
 */
function isLastChild<T>(row: TableRow<T>): boolean {
  const parent = row.getParentRow();
  if (!parent?.subRows) return false;
  return parent.subRows[parent.subRows.length - 1] === row;
}

/**
 * Operation Packageの進捗を表現するDataTableコンポーネントです。
 * 共通のスタイルをここでwrapしています。
 * 親子構造を持つことを前提に組み立てていますが、それ以外の状態が発生した場合には再度抽象化を検討してください。
 */
export function DataTableProgress<T>(props: Props<T>) {
  const { data, isPending, columns, onRowClick, className, options } = props;

  const displayState = useTableDisplayState(isPending, data);

  const table = useReactTable<T>({
    data,
    columns,
    meta: {
      getRowClassName: (row) => {
        return cn(
          row.getCanExpand()
            ? "border-none" // 親行はborderなし
            : isLastChild(row)
              ? "cursor-pointer border-neutral-border" // 最後の子要素のみborderが濃い目
              : "cursor-pointer border-neutral-muted-border",
        );
      },
      // header rowのborderなし（現状これがないとheader row間にスペースが生じてしまう）
      getHeaderAreaClassName: () => "[&_tr]:border-none",
      getHeaderCellClassName: () =>
        cn(
          "bg-neutral-gray-light-3 border-neutral-border",
          "p-2 pb-1.5 first:pl-4 last:pr-4",
          "whitespace-nowrap",
        ),
      getCellClassName: (cell: CellContext<T, unknown>): string => {
        const isHovered =
          table.getState().hoveredRowId === cell.row.id ||
          table.getState().hoveredColId === cell.column.id;

        return cn(
          isHovered && "bg-neutral-gray-light-3",
          cell.row.getCanExpand() && "px-1 py-0",
          cell && "p-3",
        );
      },
      // ホバー状態の更新
      getCellProps: (cell, table) => getHoverCellProps(cell, table),
    },
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    ...options,
    _features: [HoverFeature],
  });

  if (!isPending && data.length === 0) {
    return (
      <EmptyStateFeedBack
        {...props.emptyState}
        className="border-none rounded-none"
      />
    );
  }

  return (
    <DataTable
      table={table}
      displayState={displayState}
      onRowClick={onRowClick}
      className={cn("border-collapse")}
      rootClassName={cn(className)}
      isHoverable={false} // 既存のhover:bg-mutedを抑制
    />
  );
}
